/**
 * FollowUpTaskModal — Section 4 of the agentic-AI improvements spec.
 * Replaces the old Follow-up→/calendar redirect with an inline lightbox
 * directly on the lead focus page. Captures task type, AI-suggested
 * title/date/notes, priority, assignee, and (key feature) a *follow-up
 * contact* override that can auto-add a new contact to the lead.
 */
import React, { useState, useEffect, useMemo } from "react";
import { X, Sparkles, Check, Calendar as CalIcon, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TASK_TYPES = ["Call back", "Email", "SMS", "Send materials", "Schedule demo", "Schedule meeting", "Follow-up call", "Internal review", "Custom"];
const PRIORITIES = [
  { id: "High",   bg: "#FCEBEB", c: "#A32D2D" },
  { id: "Medium", bg: "#FAEEDA", c: "#854F0B" },
  { id: "Low",    bg: "#f0f0f0", c: "#666"    },
];
const ROLES = ["Owner", "CEO", "CMO", "COO", "Doctor", "Provider", "Nurse", "Manager", "Director", "Admin", "Accountant/Biller", "Office Manager", "IT", "Other", "Custom"];

export default function FollowUpTaskModal({ lead, defaultType = "Call back", onClose, onSave }) {
  const [type, setType] = useState(defaultType);
  const [customType, setCustomType] = useState("");
  const [title, setTitle] = useState(`${defaultType} — ${lead?.clinic || "lead"}`);
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [priority, setPriority] = useState(defaultType === "Call back" || defaultType === "Schedule demo" ? "High" : "Medium");
  const [assignee, setAssignee] = useState("Me");
  const [notes, setNotes] = useState("");
  const [usePrimary, setUsePrimary] = useState(true);
  const [contactName, setContactName] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [contactRoleCustom, setContactRoleCustom] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [addToProfile, setAddToProfile] = useState(true);
  const [contextNote, setContextNote] = useState("");
  const [drafting, setDrafting] = useState(false);

  // Auto-suggest defaults on open (Section 5C — AI pre-fill)
  useEffect(() => {
    if (!lead) return;
    // Suggest due date based on task type + stage
    const today = new Date();
    let offsetDays = 1;
    if (defaultType === "Call back") offsetDays = 1;
    else if (defaultType === "Schedule demo") offsetDays = 2;
    else if (defaultType === "Send materials") offsetDays = 1;
    else if (defaultType === "Follow-up call") offsetDays = 3;
    else offsetDays = 3;
    const d = new Date(today.getTime() + offsetDays * 86400000);
    setDueDate(d.toISOString().slice(0, 10));
    setDueTime("10:00");
    setNotes(`Follow up with ${lead?.contacts?.[0]?.name || lead?.clinic || "lead"} regarding their ${lead?.primary_product || lead?.scoring?.primary?.product || "Enable products"} fit. Reference last activity: ${lead?.activities?.[0]?.text || "no recent activity logged."}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiSuggestTitle = () => {
    setDrafting(true);
    setTimeout(() => {
      const stage = (lead?.stage || "qualification").replace(/_/g, " ");
      setTitle(`${type} ${lead?.contacts?.[0]?.name || "primary contact"} re: ${stage} → ${lead?.scoring?.primary?.product || "CCM"} pitch`);
      setDrafting(false);
      toast.success("AI title suggested — edit if needed");
    }, 500);
  };
  const aiSuggestDate = () => {
    const d = new Date(Date.now() + 86400000); setDueDate(d.toISOString().slice(0, 10)); setDueTime("10:00");
    toast.success(`Tomorrow 10 AM — typical re-engagement window for ${(lead?.stage || "qualification").replace(/_/g, " ")}`);
  };
  const aiDraftNotes = () => {
    setDrafting(true);
    setTimeout(() => {
      setNotes(`Pre-call brief\n• Lead: ${lead?.clinic || "—"} (${lead?.specialty || lead?.nppes?.specialty_group || "—"})\n• Recent: ${lead?.activities?.[0]?.text || "no recent activity"}\n• Last contact: ${lead?.last_contact_days ? `${lead.last_contact_days} days ago` : "—"}\n• AI rec: lead with ${lead?.scoring?.primary?.product || "CCM"} (${lead?.scoring?.primary?.score || "—"}/100). Bring revenue math.\n\nGoal: ${type === "Call back" ? "qualify next step" : type === "Schedule demo" ? "lock demo time" : "advance the deal"}.`);
      setDrafting(false);
      toast.success("Draft note inserted");
    }, 500);
  };

  const finalRole = contactRole === "Custom" ? contactRoleCustom : contactRole;
  const canSave = (usePrimary || contactName.trim().length > 0) && title.trim().length > 0;

  const handleSave = () => {
    if (!canSave) { toast.error("Need a title and either primary contact or a contact name"); return; }
    const finalType = type === "Custom" ? customType.trim() || "Custom" : type;
    const payload = {
      type: finalType,
      title: title.trim(),
      due: dueDate ? `${dueDate}${dueTime ? "T" + dueTime : ""}` : null,
      priority,
      assignee,
      notes,
      contact: usePrimary
        ? { primary: true }
        : { primary: false, name: contactName.trim(), role: finalRole, phone: contactPhone, email: contactEmail, addToProfile, contextNote },
    };
    onSave?.(payload);
    onClose?.();
  };

  return (
    <div data-testid="followup-modal" style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(11,17,32,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ width: 520, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" }}>
        <header style={{ padding: "14px 20px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>Create follow-up task — {lead?.clinic || "lead"}</h3>
          <button data-testid="followup-close" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#888", padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
        </header>

        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {/* Lead context bar */}
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#666", marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
            <span><strong style={{ color: "var(--navy)" }}>{lead?.clinic || "—"}</strong></span>
            <span>· {lead?.specialty || lead?.nppes?.specialty_group || "—"}</span>
            <span>· stage: {(lead?.stage || "—").toString().replace(/_/g, " ")}</span>
            {lead?.rating && <span style={{ marginLeft: "auto", padding: "1px 6px", borderRadius: 4, background: lead.rating === "Hot" ? "#FCEBEB" : lead.rating === "Warm" ? "#FAEEDA" : "#E6F1FB", color: lead.rating === "Hot" ? "#A32D2D" : lead.rating === "Warm" ? "#854F0B" : "#185FA5", fontWeight: 600, textTransform: "uppercase", fontSize: 9 }}>{lead.rating}</span>}
          </div>

          {/* Task type chips */}
          <Section label="Task type">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {TASK_TYPES.map((t) => (
                <Chip key={t} active={type === t} onClick={() => { setType(t); setTitle(`${t} — ${lead?.clinic || "lead"}`); }} testid={`fu-type-${t}`}>{t}</Chip>
              ))}
            </div>
            {type === "Custom" && (
              <input data-testid="fu-custom-type" value={customType} onChange={(e) => setCustomType(e.target.value)} placeholder="Describe the custom task..." style={{ ...inputStyle, marginTop: 8 }} />
            )}
          </Section>

          {/* Title */}
          <Section label="Task title">
            <div style={{ display: "flex", gap: 6 }}>
              <input data-testid="fu-title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <AIButton onClick={aiSuggestTitle} loading={drafting} testid="fu-title-ai" />
            </div>
          </Section>

          {/* Due + priority */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <Label>Due date</Label>
              <div style={{ display: "flex", gap: 6 }}>
                <input data-testid="fu-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              </div>
              <button onClick={aiSuggestDate} data-testid="fu-due-ai" style={{ background: "transparent", color: "#3C3489", border: "none", fontSize: 11, padding: 0, marginTop: 4, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Sparkles style={{ width: 11, height: 11 }} /> AI suggests
              </button>
            </div>
            <div>
              <Label>Priority</Label>
              <div style={{ display: "flex", gap: 6 }}>
                {PRIORITIES.map((p) => (
                  <button key={p.id} data-testid={`fu-priority-${p.id}`} onClick={() => setPriority(p.id)} style={{ flex: 1, border: "0.5px solid", borderColor: priority === p.id ? p.c : "#ddd", background: priority === p.id ? p.bg : "#fff", color: priority === p.id ? p.c : "#222", borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>{p.id}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Assignee */}
          <Section label="Assigned to">
            <select data-testid="fu-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} style={inputStyle}>
              <option>Me</option>
              <option>Sarah Patel (Sales Manager)</option>
              <option>Mike Chen (Sales Agent)</option>
              <option>Alex Garcia (Demo Agent)</option>
            </select>
          </Section>

          {/* Notes */}
          <Section label="Notes">
            <textarea data-testid="fu-notes" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: "vertical", fontFamily: "inherit" }} />
            <button onClick={aiDraftNotes} data-testid="fu-notes-ai" style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, marginTop: 6, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {drafting ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" /> : <Sparkles style={{ width: 11, height: 11 }} />} Draft with AI
            </button>
          </Section>

          {/* Follow-up contact */}
          <div style={{ borderTop: "0.5px solid #eee", paddingTop: 12, marginTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <Label style={{ marginBottom: 0 }}>Follow-up contact</Label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "#555", cursor: "pointer" }}>
                <input data-testid="fu-use-primary" type="checkbox" checked={usePrimary} onChange={(e) => setUsePrimary(e.target.checked)} /> Use primary contact
              </label>
            </div>
            {usePrimary ? (
              <div style={{ fontSize: 11, color: "#888" }}>
                Speaking with <strong style={{ color: "var(--navy)" }}>{lead?.contacts?.[0]?.name || "primary contact"}</strong> ({lead?.contacts?.[0]?.role || "primary"})
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <Label small>Contact name *</Label>
                    <input data-testid="fu-c-name" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Full name" style={inputStyle} />
                  </div>
                  <div>
                    <Label small>Role / title</Label>
                    <select data-testid="fu-c-role" value={contactRole} onChange={(e) => setContactRole(e.target.value)} style={inputStyle}>
                      <option value="">Select role…</option>
                      {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>
                    {contactRole === "Custom" && <input value={contactRoleCustom} onChange={(e) => setContactRoleCustom(e.target.value)} placeholder="Custom role" style={{ ...inputStyle, marginTop: 4 }} />}
                  </div>
                  <div>
                    <Label small>Phone</Label>
                    <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(XXX) XXX-XXXX" style={inputStyle} />
                  </div>
                  <div>
                    <Label small>Email</Label>
                    <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="email@practice.com" style={inputStyle} />
                  </div>
                </div>
                <label style={{ fontSize: 11, color: "#555", display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", marginBottom: 8 }}>
                  <input data-testid="fu-add-profile" type="checkbox" checked={addToProfile} onChange={(e) => setAddToProfile(e.target.checked)} /> Automatically add <strong style={{ color: "var(--navy)" }}>{contactName || "this contact"}</strong> to the lead profile
                </label>
                <Label small>Context note (optional)</Label>
                <textarea data-testid="fu-c-context" value={contextNote} onChange={(e) => setContextNote(e.target.value)} placeholder="e.g. 'Front office said to speak with John — he's responsible for buying decisions and is available tomorrow at 12 PM.'" style={{ ...inputStyle, minHeight: 50, resize: "vertical", fontFamily: "inherit" }} />
              </>
            )}
          </div>
        </div>

        <footer style={{ padding: "12px 20px", borderTop: "0.5px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end", background: "#FAFBFD" }}>
          <button onClick={onClose} style={btnOutline}>Cancel</button>
          <button data-testid="fu-save" onClick={handleSave} disabled={!canSave} style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5, cursor: canSave ? "pointer" : "not-allowed" }}>
            <Check style={{ width: 13, height: 13 }} /> Save task
          </button>
        </footer>
      </div>
    </div>
  );
}

const inputStyle = { width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", color: "#222", background: "#fff", boxSizing: "border-box" };
const btnOutline = { background: "#fff", color: "#222", border: "0.5px solid #ddd", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" };
const btnPrimary = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 };

function Section({ label, children }) { return <div style={{ marginBottom: 14 }}><Label>{label}</Label>{children}</div>; }
function Label({ children, small, style }) { return <div style={{ fontSize: small ? 9 : 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.04em", fontWeight: 600, marginBottom: 6, ...style }}>{children}</div>; }
function Chip({ children, active, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} style={{ border: "0.5px solid", borderColor: active ? "#2563eb" : "#ddd", background: active ? "#E6F1FB" : "#fff", color: active ? "#185FA5" : "#222", borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>{children}</button>;
}
function AIButton({ onClick, loading, testid }) {
  return <button data-testid={testid} onClick={onClick} title="AI suggest" style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 8, padding: "0 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
    {loading ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" /> : <Sparkles style={{ width: 11, height: 11 }} />} AI
  </button>;
}
